import { AnnotationMode } from './editor-annotations';

const _isIE11 = typeof window === 'undefined' ? false : !!(<any>globalThis).MSInputMethodContext && !!(<any>document).documentMode;
const isEdge = typeof navigator === 'undefined' || /Edge\/\d./i.test(navigator.userAgent);
const needsES5 = typeof ReadableStream === 'undefined' || typeof Promise['allSettled'] === 'undefined';

export const pdfjsVersion = '6.4.1351';
export const pdfjsBleedingEdgeVersion = '6.5.1205';
export function getVersionSuffix(folder: string): string {
  if (folder?.includes('bleeding-edge')) {
    return pdfjsBleedingEdgeVersion;
  }
  return pdfjsVersion;
}

export function assetsUrl(url: string, postfixIfPathIsRelativ = ''): string {
  if (url.includes('://')) {
    // the assets folder is on an absolute path (like https://example.com/assets)
    return url;
  }
  return `./${url + postfixIfPathIsRelativ}`;
}

// #3209: a relative asset URL such as "./assets/pdf.worker-x.min.mjs" is
// resolved by pdf.js (and the browser's `new Worker()`) against the current
// route or, with a `<base href>`, gets the base applied twice — producing
// mangled URLs like ".../crossdomainproxy//crossdomainproxy/../...". Resolving
// against `document.baseURI` here yields a stable absolute URL, mirroring the
// wasmUrl (#3140) fix. Absolute folders and SSR (no `document`) keep the exact
// string `assetsUrl()` already produced, so existing setups are unaffected.
export function resolveAssetUrlAgainstBaseHref(composedUrl: string): string {
  if (composedUrl.startsWith('./') && typeof document !== 'undefined') {
    return new URL(composedUrl, document.baseURI).href;
  }
  return composedUrl;
}

export function isBleedingEdge(): boolean {
  return pdfDefaultOptions.assetsFolder?.includes('bleeding-edge');
}

/**
 * True when the pdf.js engine that will be loaded is at least the given
 * `major.minor` version. Prefer this over {@link isBleedingEdge} for gating a
 * feature: the bleeding-edge/stable split is a moving target (stable advances
 * over time), whereas a feature is permanently tied to the pdf.js version that
 * introduced it. The version is derived from the bundle that `assetsFolder`
 * selects (`getVersionSuffix`).
 */
export function isPdfjsVersionAtLeast(major: number, minor: number): boolean {
  const [maj = 0, min = 0] = getVersionSuffix(pdfDefaultOptions.assetsFolder)
    .split('.')
    .map((part) => parseInt(part, 10) || 0);
  return maj > major || (maj === major && min >= minor);
}

declare const process: any;

function isTestEnvironment(): boolean {
  return (
    typeof process !== 'undefined' &&
    typeof (process as any).env !== 'undefined' &&
    ((process as any).env.NODE_ENV === 'test' || (process as any).env.JEST_WORKER_ID !== undefined || (process as any).env.VITEST !== undefined)
  );
}

export function getSafeCanvasSize(): number {
  if (typeof window === 'undefined' || typeof document === 'undefined' || isTestEnvironment()) {
    return 4096;
  }

  // Use PDF.js defaults for maximum compatibility
  // The IOSCanvasOptimizationService handles dynamic optimization
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
  const isMobile = /Android|iPhone|iPad|iPod/.test(navigator.userAgent);

  if (isIOS || isMobile) {
    return 5242880; // PDF.js iOS/Android limit (5 megapixels)
  }

  return 33554432; // PDF.js desktop default (32 megapixels)
}

// sonar ignore next line
export const pdfDefaultOptions = {
  needsES5: _isIE11 || isEdge || needsES5,
  annotationEditorMode: 0,
  annotationMode: AnnotationMode.ENABLE_FORMS,
  defaultZoomDelay: 400, // milliseconds
  cursorToolOnLoad: 0,
  defaultUrl: '',
  defaultZoomValue: '',
  disableHistory: false,
  disablePageLabels: false,
  enableComment: true,
  enablePermissions: false,
  docBaseUrl: '',
  enablePrintAutoRotate: true,
  enableSignatureEditor: false,
  externalLinkRel: 'noopener noreferrer nofollow',
  externalLinkTarget: 0,
  findController: undefined as any, // must extend PDFFindController
  historyUpdateUrl: false,
  ignoreDestinationZoom: false,
  imageResourcesPath: './images/',
  maxCanvasPixels: getSafeCanvasSize(),
  forcePageColors: false,
  pageColorsBackground: 'Canvas',
  pageColorsForeground: 'CanvasText',
  pdfBugEnabled: false,
  printResolution: 150,
  rangeChunkSize: 65536,
  removePageBorders: false,
  enableXfa: true,
  fontExtraProperties: false,
  sidebarViewOnLoad: -1,
  scrollModeOnLoad: -1,
  spreadModeOnLoad: -1,
  textLayerMode: 1,
  // viewerCssTheme: 0, // not supported by ngx-extended-pdf-viewer, use [theme] instead
  viewOnLoad: 0,
  cMapPacked: true,
  // #3232 (see the comment on sandboxBundleSrc below): the `/..` cancelled out
  // the assets folder, so the cmaps and the standard fonts were looked up as
  // siblings of that folder. They ship *inside* it, exactly like the wasm files.
  // The mistake used to be masked because the resulting relative path was
  // resolved against the current route; since #3209 it resolves against
  // `document.baseURI`, which turns it into a plain 404 at the site root.
  cMapUrl: function () {
    return resolveAssetUrlAgainstBaseHref(`${assetsUrl(pdfDefaultOptions.assetsFolder)}/cmaps/`);
  },
  disableAutoFetch: false,
  disableFontFace: false,
  disableRange: false,
  disableStream: true,
  isOffscreenCanvasSupported: true,
  maxImageSize: -1,
  pdfBug: false,
  verbosity: 1,
  workerPort: null as any,
  assetsFolder: 'assets',
  _internalFilenameSuffix: '.min', // don't modify this - it's an internal field
  // #3232: unlike workerSrc, this path was missing the assets-folder prefix
  // (`./pdf.sandbox-…` instead of `./assets/pdf.sandbox-…`). It used to work by
  // accident because pdf.js's dynamic `import()` resolved the bare specifier
  // relative to the viewer module (which lives in the assets folder). Once
  // #3209 started resolving against `document.baseURI` (the page root), the
  // missing folder made it 404 at the site root, so the scripting sandbox no
  // longer loaded and JS embedded in PDFs stopped running. Prefix the folder
  // like workerSrc does so it resolves to the file that actually ships.
  sandboxBundleSrc: function () {
    return resolveAssetUrlAgainstBaseHref(
      pdfDefaultOptions.needsES5
        ? `${assetsUrl(pdfDefaultOptions.assetsFolder)}/pdf.sandbox-${getVersionSuffix(assetsUrl(pdfDefaultOptions.assetsFolder))}-es5.mjs`
        : `${assetsUrl(pdfDefaultOptions.assetsFolder)}/pdf.sandbox-${getVersionSuffix(assetsUrl(pdfDefaultOptions.assetsFolder))}${pdfDefaultOptions._internalFilenameSuffix}.mjs`
    );
  },
  workerSrc: function () {
    return resolveAssetUrlAgainstBaseHref(
      pdfDefaultOptions.needsES5
        ? `${assetsUrl(pdfDefaultOptions.assetsFolder)}/pdf.worker-${getVersionSuffix(assetsUrl(pdfDefaultOptions.assetsFolder))}-es5.mjs`
        : `${assetsUrl(pdfDefaultOptions.assetsFolder)}/pdf.worker-${getVersionSuffix(assetsUrl(pdfDefaultOptions.assetsFolder))}${
            pdfDefaultOptions._internalFilenameSuffix
          }.mjs`
    );
  },
  // pdf.js 6.5+ can render pages in a second web worker (OffscreenCanvas).
  // Experimental and off by default, like upstream; set `disableWorkerRendering`
  // to false to try it. Older engines ignore both options.
  disableWorkerRendering: true,
  rendererSrc: function () {
    return resolveAssetUrlAgainstBaseHref(
      pdfDefaultOptions.needsES5
        ? `${assetsUrl(pdfDefaultOptions.assetsFolder)}/pdf.renderer-${getVersionSuffix(assetsUrl(pdfDefaultOptions.assetsFolder))}-es5.mjs`
        : `${assetsUrl(pdfDefaultOptions.assetsFolder)}/pdf.renderer-${getVersionSuffix(assetsUrl(pdfDefaultOptions.assetsFolder))}${
            pdfDefaultOptions._internalFilenameSuffix
          }.mjs`
    );
  },
  // #3232: see the comment on cMapUrl - the standard fonts ship inside the
  // assets folder, too.
  standardFontDataUrl: () => resolveAssetUrlAgainstBaseHref(`${assetsUrl(pdfDefaultOptions.assetsFolder)}/standard_fonts/`),
  // #3275: the ICC profile pdf.js uses to convert DeviceCMYK colors ships inside
  // the assets folder, too. pdf.js's default (`../web/iccs/`) is resolved by the
  // worker against its own URL, so it pointed to a folder that doesn't exist.
  iccUrl: () => resolveAssetUrlAgainstBaseHref(`${assetsUrl(pdfDefaultOptions.assetsFolder)}/iccs/`),
  // #3140: wasm files live inside the assets folder (not as a sibling).
  // Resolve against document.baseURI so the path stays correct on sub-routes
  // (pdf.js's QuickJS loader resolves wasmUrl against `location.href`, which
  // otherwise turns `./assets/wasm/` into `/<route>/assets/wasm/`).
  wasmUrl: () => {
    const folder = pdfDefaultOptions.assetsFolder;
    if (folder?.includes('://')) {
      return `${folder}/wasm/`;
    }
    if (typeof document !== 'undefined') {
      return new URL(`${folder}/wasm/`, document.baseURI).href;
    }
    return `./${folder}/wasm/`;
  },

  // options specific to ngx-extended-pdf-viewer (as opposed to being used by pdf.js)
  doubleTapZoomFactor: 'page-width',
  doubleTapZoomsInHandMode: true,
  doubleTapZoomsInTextSelectionMode: false,
  doubleTapResetsZoomOnSecondDoubleTap: false,
  enableScripting: false,
  enableCatalogAAJavaScript: false,
  enableOpenActionJavaScript: false,
  defaultCacheSize: 50,
  passwordPrompt: undefined as any,
  enableHWA: true, // enable hardware acceleration. Active since pdf.js 4.4, default true since pdf.js 5.6.
  enableWebGPU: true, // enable WebGPU mesh shading for improved rendering performance
  enableNewBadge: false, // show a "New" badge on the manage button (upstream default for MOZCENTRAL only)
  imagesRightClickMinSize: -1, // minimum size (px) for right-clickable images in text layer; -1 = disabled
  positionPopupDialogsWithJavaScript: true,
  enablePageReordering: false, // allows users to reorder pages by dragging thumbnails
  enableSplitMerge: false, // allows users to copy, cut, delete, and export selected pages
  enableMerge: false, // adds an "Add file" button to the sidebar that merges another PDF/image into the current document
  useWasm: true, // set to false to disable WebAssembly for image decoding (uses *_nowasm_fallback.js instead). Required for CSPs that forbid `wasm-unsafe-eval`.
  enableAltText: false, // no effect in ngx-extended-pdf-viewer: in pdf.js, it shows the settings of Firefox's built-in AI alt-text generation. The "Alt text" button of images is always there.
  enableAutoLinking: true, // detects URLs and email addresses in the text layer and turns them into clickable links
  enableHighlightFloatingButton: false, // shows a floating "Highlight" shortcut next to selected text (experimental upstream)
  pdfBackgroundColor: '', // background color for PDF content rendering
};
